import { inject } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type TransformsElementProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type ColumnId } from '@blaze-shared/api-contracts-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelPropsWithPositionAndWidth,
  type ElementLabelStyleProps,
  type ElementSizingMode,
  type FieldSortConfig,
  getActiveBinding,
  isBindingActive,
  type OptionPickerDefaultValue,
  OPTIONS_PICKER_DROPDOWN_DEFAULT_VALUE_OPTIONS,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { GET_TIMEZONES_API_ID } from '@blaze-shared/platform-data-access';
import { COLUMN_ID_OR_NAME } from '@blaze-shared/schema-domain';
import { type SelectOption, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { updateDropdownDynamicSearchTry } from '../../+state/elements/elements.actions';
import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { DropdownDefinitionMapper } from './ai/dropdown.definition-mapper';
import { DropdownElementHandler } from './ai/dropdown.element-handler';
import { DropdownComponent } from './dropdown.component';
import { DropdownPropsBuilder } from './dropdown.props-builder';
import { generateDropdownFormField } from './generation/form-field';
import { dropdownV2Migration } from './migrations/002-dropdown.migration';
import { dropdownV3PaddingToMarginMigration } from './migrations/003-dropdown-padding-to-margin.migration';

export { transformToDropdown } from './dropdown.transforms';
export type DropdownElement = CanvasElementFrom<'dropdown'>;
export type DropdownDef = WithProps<DropdownPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    dropdown: DropdownDef;
  }
}

export const DROPDOWN_USER_INPUT_NAME = 'dropdownUserInput';
export const DROPDOWN_DYNAMIC_SEARCH_CONDITION_ID = 'DROPDOWN_DYNAMIC_SEARCH';

const DEFAULT_WIDTH = 356;
const DEFAULT_HEIGHT = 48;
const MIN_SIZE = 40;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['min'];

registerElementDefinition({
  type: 'dropdown',
  componentType: DropdownComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Dropdown',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: DropdownPropsBuilder,
  generation: {
    formField: generateDropdownFormField,
  },
  groups: ['custom'],
  styleIncludePaths: ['style'],
  metaUiFeatures: {
    type: 'input',
    paths: {
      readonly: 'content.data.readonly',
      placeholder: 'content.menu.placeholder',
    },
  },
  features: {
    ai: {
      showAiPromptAutomatically: true,
      optionalInput: true,
    },
    optionsSortSupport: true,
    searchAndFiltersFieldSupport: true,
  },
  ai: {
    name: 'dropdown',
    handler: () => DropdownElementHandler,
    definitionMapper: () => DropdownDefinitionMapper,
  },
  version: 3,
  migrations: {
    2: dropdownV2Migration,
    3: dropdownV3PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'optionsList',
        isDefaultDataSourceInput: true,
        isQueryable: true,
        bindingDefs: [
          { property: 'value', label: 'Option value field', dataType: 'String', value: true },
          { property: 'label', label: 'Option label field', dataType: 'String', display: true },
        ],
      },
      {
        name: 'selected',
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'selected', label: 'Selected value', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              name: 'label',
              isProfilePicture: false,
            },
            {
              columnType: 'String',
              dataType: 'String',
              name: 'value',
              isProfilePicture: false,
            },
          ],
          id: '',
          name: 'Dropdown',
        },
      }, // schema set in dropdown.component
      {
        eventType: 'input',
        debounce: 300,
        schema: {
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              name: '',
              isProfilePicture: false,
            },
          ],
          id: '',
          name: 'User input',
        },
      },
      {
        eventType: 'query',
        schema: null,
      },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  StyleConfigProps &
  VisibilityElementProps &
  TransformsElementProps;

export type DropdownPropsModel = PropsModel & {
  content: {
    title: ElementLabelPropsWithPositionAndWidth;
    data: {
      readonly?: boolean;
      defaultValue: OptionPickerDefaultValue;
    };
    sort: FieldSortConfig | FieldSortConfig[];
    menu: {
      placeholder: string;
      values: string[];
      preselect: string | undefined;
      prefix: string;
      suffix: string;
      /** null if option not available for current data source */
      dynamicSearch: boolean | null;
    };
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      title: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    labelBlock: ElementLabelStyleProps;
    input: {
      color: PropertiesConfigInputColorModel;
      fill: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
    list: {
      value: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
  };
};

function getDefaults(): PropsOf<'dropdown'> {
  return {
    content: {
      title: {
        label: 'Dropdown',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      data: {
        defaultValue: 'none',
      },
      sort: {
        field: '',
        direction: '',
      },
      menu: {
        placeholder: '',
        values: [],
        preselect: 'Option 1',
        prefix: '',
        suffix: '',
        dynamicSearch: false,
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      input: {
        color: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
      list: {
        value: { color1: '$t.value', selectorType: 'solid' },
        background: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('dropdown', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:optionsList.enabled',
        'Data',
        [epc.fields.listItemValues('content.menu.values', 'Values')],
        [
          // Hide data source options if the element is bound to tags column
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectDataSourceField({
                key: 'optionsList',
                label: 'Values',
                sources: 'list',
                bindings: [
                  {
                    property: 'value',
                    label: 'Option value field',
                    dataType: 'String',
                    value: true,
                  },
                  {
                    property: 'label',
                    label: 'Option label field',
                    dataType: 'String',
                    display: true,
                  },
                ],
              }),
              ...epc.utils.conditionalFields(
                [
                  epc.utils.observedField(
                    epc.fields.selectFieldFromSchema(
                      'data.?key:optionsList.bindings.label',
                      'Display',
                      'data.?key:optionsList.dataSource',
                      {
                        valueField: COLUMN_ID_OR_NAME,
                        optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                      },
                    ),
                    (model: DropdownPropsModel, value: ColumnId | string, syncModel, elementId) => {
                      const columnKey = value;
                      const isDynamicSearchActive = !!model.content.menu.dynamicSearch;

                      if (isDynamicSearchActive && elementId && columnKey) {
                        inject(Store).dispatch(
                          updateDropdownDynamicSearchTry({
                            elementId,
                            columnKey,
                            enable: isDynamicSearchActive,
                          }),
                        );
                      }

                      return {};
                    },
                  ),
                  epc.fields.sortField(
                    'content.sort',
                    'Sort by',
                    'data.?key:optionsList.dataSource.columns',
                    'name',
                    COLUMN_ID_OR_NAME,
                  ),
                  epc.utils.conditionalField(
                    epc.utils.disabledField(
                      epc.utils.observedField(
                        epc.fields.toggleField('content.menu.dynamicSearch', 'Dynamic search', {
                          tooltip:
                            'Fetch and filter options as the user types them. Improves performance for large datasets.',
                        }),
                        (model: DropdownPropsModel, value, syncModel, elementId) => {
                          const oldValue = !!model.content.menu.dynamicSearch;
                          const columnKey = getActiveBinding(model.data, 'optionsList')?.bindings?.[
                            'label'
                          ];

                          if (oldValue !== value && elementId && columnKey) {
                            inject(Store).dispatch(
                              updateDropdownDynamicSearchTry({
                                elementId,
                                columnKey,
                                enable: value,
                              }),
                            );
                          }

                          return {};
                        },
                      ),
                      (model) =>
                        model.content.menu.dynamicSearch === null
                          ? 'This source does not support dynamic search'
                          : undefined,
                    ),
                    'data.?key:optionsList',
                    // Dynamic search can be available for workflow or dataset sources only
                    (value) =>
                      !isBindingActive(value) ||
                      (value?.dataSource?.sourceType !== 'context' &&
                        value?.dataSource?.sourceType !== 'dataset'),
                  ),
                ],
                'data.?key:optionsList.enabled',
                (value) => !value,
              ),
            ],
            'data.?key:optionsList',
            (value) =>
              !isBindingActive(value) ||
              value?.dataSource?.sourceType === 'column-tags' ||
              value?.dataSource?.sourceType === 'column-filter' ||
              value?.dataSource?.sourceType === 'column-filter-ranged' ||
              (value?.dataSource?.sourceType === 'api-call' &&
                value.dataSource.id === GET_TIMEZONES_API_ID),
          ),
        ],
      ),

      epc.subsections.subsection(
        'content.menu',
        'Value',
        [
          epc.fields.selectFieldFromList(
            'content.data.defaultValue',
            'Default value',
            OPTIONS_PICKER_DROPDOWN_DEFAULT_VALUE_OPTIONS,
            {
              showSelection: true,
              allowNone: false,
              optionsFilter: (model: PropsOf<'dropdown'>, options) => {
                const isOptionListEnabled = getValueByPath(model, `data.?key:optionsList.enabled`);
                return !isOptionListEnabled || isBoundToTimeZones(model)
                  ? options
                  : (options as SelectOption<OptionPickerDefaultValue>[])?.filter(
                      (o) => o.value !== 'fixedValue',
                    );
              },
            },
          ),
          epc.utils.conditionalField(
            epc.fields.textInput('content.menu.placeholder', 'Placeholder'),
            'content.data.defaultValue',
            (modelValue) => (modelValue as OptionPickerDefaultValue) !== 'placeholder',
          ),
          epc.utils.conditionalField(
            // pick from list of `values`
            epc.fields.selectFieldFromModel(
              'content.menu.preselect',
              'Value',
              'content.menu.values',
              { allowNone: false, showSelection: true },
            ),
            'content.data.defaultValue',
            (modelValue: OptionPickerDefaultValue, model): boolean => {
              return modelValue !== 'fixedValue' || isBoundToTimeZones(model);
            },
          ),
          epc.utils.conditionalField(
            // pick from timezones
            epc.fields.selectFieldFromApi('content.menu.preselect', 'Value', 'LIST_TIMEZONES', {
              allowNone: false,
              showSelection: true,
            }),
            'content.data.defaultValue',
            (modelValue: OptionPickerDefaultValue, model): boolean => {
              return modelValue !== 'fixedValue' || !isBoundToTimeZones(model);
            },
          ),
          epc.utils.observedField(
            epc.fields.toggleField('content.data.readonly', 'Read Only'),
            (_, readonly: boolean) => {
              return {
                ['style.input.border.color1']: readonly ? 'transparent' : '$t.outline',
                ['style.input.fill.color1']: readonly ? 'transparent' : '$t.fillInternal',
              };
            },
          ),
          // hidden for now
          // epc.fields.textInput('content.menu.prefix', 'Prefix'),
          // epc.fields.textInput('content.menu.suffix', 'Suffix'),
        ],
        {
          dataBindings: [
            {
              key: 'selected',
              label: 'Selected Value',
              sources: 'field',
              bindings: [{ property: 'selected', label: 'Selected value', dataType: 'String' }],
            },
          ],
        },
      ),

      epc.subsections.visibilitySubsection(),

      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection('style.input', 'Input', [
          epc.fields.colorField('style.input.color', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.input.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.input.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.input.radius'),
        ]),

        epc.subsections.subsection('style.list', 'List', [
          epc.fields.colorField('style.list.value', 'Value', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.list.background', 'Background', { allowNone: false }),
          epc.fields.colorField('style.list.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.list.radius'),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}

function getInitial(): PartialPropsOf<'dropdown'> {
  return {
    content: {
      title: {
        width: '35%',
      },
      menu: {
        values: ['Option 1', 'Option 2', 'Option 3'],
      },
    },
  };
}

function isBoundToTimeZones(model: any): boolean {
  return getActiveBinding(model.data, 'optionsList')?.dataSource?.id === GET_TIMEZONES_API_ID;
}
